import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { appBuildManager } from '../services/AppBuildManager';
import { BackgroundSaveService } from '../services/backgroundSaveService';

/** How often to re-check whether it has become safe to reload on its own. */
const SAFETY_RECHECK_MS = 2000;

/**
 * Whether this tab can be reloaded right now without destroying work in progress.
 *
 * Two conditions, because neither is sufficient alone:
 *
 *  - `hasWorkPending()` covers dirty/in-flight/retry-scheduled state in the save engine.
 *  - The `/wizard/` path check covers what the engine cannot see: several wizard pages hold edits
 *    in page-local state behind their own debounce (NetWorth 1000ms text / 50ms numeric, Budget's
 *    shared 1000ms expense timer) before ever marking the engine dirty, and Manual Save mode holds
 *    everything unsaved indefinitely by design. On any wizard route we always ask rather than act.
 *
 * A blind reload is not a neutral act here: on a wizard page with unsaved changes it trips the
 * beforeunload handler in useNavigationGuard, which shows an unexplained native "Leave site?"
 * dialog and then reloads anyway. In Manual Save mode every unload rescue path is deliberately
 * disabled, so it would be silent data loss.
 */
function isSafeToReloadNow(): boolean {
    if (BackgroundSaveService.hasWorkPending()) {
        return false;
    }
    return !window.location.pathname.includes('/wizard/');
}

/**
 * Reacts to this tab running an out-of-date bundle: reloads silently when that is safe, and
 * otherwise shows a banner so the user chooses the moment.
 *
 * Staleness itself is detected by AppBuildManager, fed by the API's X-Calculist-Git-Commit
 * response header and by the /version.json poll in hooks/useVersionCheck.ts.
 *
 * Mounted above the router (App.tsx), because SimpleCaseProvider is per-wizard-route and so case
 * context is not reachable from here -- which is why the safety check goes through the save
 * service singleton rather than through `hasUnsavedChanges`.
 */
export const StaleBuildReloader: React.FC = () => {
    const theme = useTheme();
    const [showBanner, setShowBanner] = useState(false);

    const reloadNow = useCallback(() => {
        appBuildManager.reloadNow();
    }, []);

    useEffect(() => {
        let recheckTimer: ReturnType<typeof setInterval> | null = null;

        const stopRechecking = () => {
            if (recheckTimer !== null) {
                clearInterval(recheckTimer);
                recheckTimer = null;
            }
        };

        /**
         * Reload if we safely can, otherwise leave the banner up and try again shortly.
         * @returns whether it is worth calling again later
         */
        const act = (): boolean => {
            if (!appBuildManager.canAttemptReload()) {
                // We already reloaded for this build and came back still stale -- reloading again
                // would loop. Leave the banner up so the user is at least told, and stop retrying.
                setShowBanner(true);
                return false;
            }

            if (isSafeToReloadNow()) {
                appBuildManager.reloadNow();
                return false;
            }

            setShowBanner(true);
            return true;
        };

        const unsubscribe = appBuildManager.subscribe((stale) => {
            if (!stale) {
                return;
            }

            if (!act()) {
                stopRechecking();
                return;
            }

            // The user may be mid-edit now but done in a moment. Once the save settles and they
            // leave the wizard, pick up the new build without making them click anything.
            if (recheckTimer === null) {
                recheckTimer = setInterval(() => {
                    if (!act()) {
                        stopRechecking();
                    }
                }, SAFETY_RECHECK_MS);
            }
        });

        return () => {
            unsubscribe();
            stopRechecking();
        };
    }, []);

    if (!showBanner) {
        return null;
    }

    return (
        <div
            role="alert"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 2000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing.sm,
                padding: theme.spacing.sm,
                backgroundColor: theme.colors.status.warning,
                color: theme.colors.text.inverse,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
            }}
        >
            <i className="fas fa-triangle-exclamation" aria-hidden />
            <span>A new version of Calculist is available. Your numbers may be out of date.</span>
            <button
                type="button"
                onClick={reloadNow}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.xs,
                    background: 'none',
                    border: `1px solid ${theme.colors.text.inverse}`,
                    borderRadius: theme.borderRadius.sm,
                    color: theme.colors.text.inverse,
                    cursor: 'pointer',
                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                }}
            >
                <i className="fas fa-rotate-right" aria-hidden />
                Reload now
            </button>
        </div>
    );
};
